import { Suspense, useMemo } from 'react';

import { Box, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useMatches, useSearchParams } from 'react-router-dom';

import ActionBar from '~components/actionBar/actionBar';
import { ActionBarProvider } from '~components/actionBar/actionBarProvider';
import GeneralLoader from '~components/loaders/generalLoader';
import { usePageTitle } from '~hooks/usePageTitle';
import { Crumb } from '~layouts/interfaces/crumb';
import { PageHeaderInfo } from '~layouts/interfaces/pageHeaderInfo';
import PageHeader from '~layouts/main/headers/pageHeader';
import Breadcrumbs from '~layouts/main/navigation/breadcrumbs';
import TabBar, { TabData } from '~layouts/main/navigation/tabBar';
import Dimens from '~themes/dimens';

interface MatchHandle {
	crumb: (match?: unknown) => string;
	crumbPath?: (match: { pathname: string }) => string;
}

interface PageLayoutProps {
	isLoading?: boolean;
	pageTitle?: string;
	crumbs?: Crumb[];
	activeTabKey?: string;
	tabs?: TabData[];
	children?: React.ReactNode;
	/**
	 * Allows resolving a human-friendly titles and visuals for details pages.
	 * Note: This performs an extra call to load the entity. Using SWR in
	 * routes is not allowed (routes are not React elements), so the title
	 * resolves asynchronously.
	 * - With getHeaderInfo: the last breadcrumb segment is hidden until the title
	 *   resolves to avoid flicker (no temporary ID shown).
	 * - Without getHeaderInfo: the ID is used immediately for the last segment.
	 *
	 * @param id Id of the entity that can be used to load the entity
	 * @returns The getHeaderInfo that is to be displayed for this page.
	 */
	headerInfo?: PageHeaderInfo;
	/** Hides the large on-page header info (image, title, subtitle) (keeps breadcrumbs) */
	hideHeaderInfo?: boolean;
	/**
	 * Specify which url query params (search params) are preserved while navigating between tabs on the page.
	 */
	preservedTabSearchParams?: string[];
	/** Hide breadcrumb trail entirely */
	hideBreadcrumbs?: boolean;

	/**
	 * Disables the default padding applied to the scrollable content area.
	 * Useful for special layouts that want full control over spacing.
	 */
	disableContentPadding?: boolean;

	/**
	 * Disables the default scroll container (overflowY: auto) for the page content.
	 * Useful for layouts that want to manage scrolling internally (e.g. split-pane pages).
	 */
	disableContentScroll?: boolean;
}

const PageLayout = ({
	isLoading = false,
	pageTitle,
	crumbs: newCrumbs,
	activeTabKey = '',
	tabs,
	children,
	headerInfo,
	preservedTabSearchParams,
	hideHeaderInfo = false,
	hideBreadcrumbs = false,
	disableContentPadding = false,
	disableContentScroll = false,
}: PageLayoutProps) => {
	const theme = useTheme();
	const { t } = useTranslation();
	const [searchParams] = useSearchParams();
	const matches = useMatches();

	usePageTitle(pageTitle ?? '');

	const preservedBreadcrumbSearch = useMemo(() => {
		const nextSearch = new URLSearchParams();
		for (const param of preservedTabSearchParams ?? []) {
			const value = searchParams.get(param);
			if (value != null && value.length > 0) {
				nextSearch.set(param, value);
			}
		}

		const serialized = nextSearch.toString();
		return serialized.length > 0 ? `?${serialized}` : '';
	}, [preservedTabSearchParams, searchParams]);

	const crumbs = useMemo(() => {
		if (newCrumbs) {
			return newCrumbs;
		}

		const baseCrumbs: Crumb[] = matches
			.filter((match) => Boolean(match.handle && (match.handle as MatchHandle).crumb))
			.map((match) => ({
				title: (match.handle as MatchHandle).crumb(match),
				path:
					(match.handle as MatchHandle).crumbPath?.({ pathname: match.pathname }) ??
					match.pathname.slice(0, match.pathname.lastIndexOf('/')),
			}));

		const headerTitle = headerInfo?.title;
		const leafMatch = matches[matches.length - 1];
		const idParam = leafMatch?.params?.id;
		const leafParams = leafMatch?.params ?? {};
		const definedLeafParamKeys = Object.keys(leafParams).filter((key) => leafParams[key] != null);

		const shouldAppendHeaderTitleAsCurrentCrumb =
			headerTitle != null &&
			definedLeafParamKeys.length === 1 &&
			definedLeafParamKeys[0] === 'id' &&
			baseCrumbs[baseCrumbs.length - 1]?.title !== headerTitle;

		if (shouldAppendHeaderTitleAsCurrentCrumb) {
			return [
				...baseCrumbs,
				{
					title: headerTitle,
					path: leafMatch?.pathname ?? '',
				},
			];
		}

		const shouldAppendIdAsCurrentCrumb =
			headerTitle == null &&
			idParam != null &&
			definedLeafParamKeys.length === 1 &&
			definedLeafParamKeys[0] === 'id';

		if (shouldAppendIdAsCurrentCrumb) {
			return [
				...baseCrumbs,
				{
					title: idParam,
					path: leafMatch?.pathname ?? '',
				},
			];
		}

		return baseCrumbs;
	}, [matches, headerInfo?.title]);

	// Compute display flags and title
	const showBreadcrumbs = !hideBreadcrumbs && crumbs.length > 0;

	const activeTabComponent = useMemo(() => {
		return tabs?.find((tab) => tab.key == activeTabKey)?.component;
	}, [activeTabKey, tabs]);

	return (
		<ActionBarProvider>
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					overflow: 'hidden',
					backgroundColor: theme.palette.bgSecondary.main,
					height: '100%',
				}}
			>
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						px: {
							xs: Dimens.spacingLg,
							md: Dimens.spacing4xl,
						},
						pt: {
							xs: Dimens.spacingLg,
							md: Dimens.spacing4xl,
						},
					}}
				>
					{showBreadcrumbs && (
						<Breadcrumbs
							isLoading={isLoading}
							crumbs={crumbs}
							preservedBreadcrumbSearch={preservedBreadcrumbSearch}
						/>
					)}

					<Box
						sx={{
							display: 'flex',
							flexDirection: 'row',
							justifyContent: 'space-between',
							alignItems: 'center',
							mt: Dimens.spacing3xl,
						}}
					>
						{!hideHeaderInfo && <PageHeader isLoading={isLoading} headerInfo={headerInfo} />}
						<ActionBar />
					</Box>

					{tabs && (
						<TabBar
							activeTabKey={activeTabKey}
							tabs={tabs}
							preservedTabSearchParams={preservedTabSearchParams}
						/>
					)}
				</Box>
				<Box
					sx={{
						display: 'block',
						overflowY: disableContentScroll ? 'hidden' : 'auto',
						...(disableContentPadding ?
							{
								p: 0,
							}
						:	{
								// Consistent padding for all pages. Page-specific spacing should live in containers.
								p: {
									xs: Dimens.spacingMd,
									md: Dimens.spacing4xl,
								},
							}),
						height: '100%',
					}}
				>
					<Suspense fallback={<GeneralLoader title={t('loading', { ns: 'status' })} />}>
						{isLoading ?
							<GeneralLoader title={t('loading', { ns: 'status' })} />
						:	(activeTabComponent ?? children)}
					</Suspense>
				</Box>
			</Box>
		</ActionBarProvider>
	);
};

export default PageLayout;
